import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, StickyNote } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/context/LanguageContext";
import { fmtDate } from "@/lib/formatters";
import { StatusBadge, LEAD_STATUSES, normalizeStatus } from "@/components/admin/StatusBadge";

const NOTES_MAX = 2000;

const Row = ({ label, value, className = "" }) => (
  <div className="grid grid-cols-3 gap-2">
    <span className="font-semibold text-[var(--muted-ink)]">{label}</span>
    <span className={`col-span-2 text-[var(--brand-charcoal)] ${className}`}>{value}</span>
  </div>
);

/**
 * Lead details + admin-only editing of the status marker and private notes.
 * `onSave(leadId, patch)` must return a promise (see useAdminData.updateLead).
 */
export const LeadDetailsDialog = ({ lead, onClose, onSave }) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState("new");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Re-seed the editable fields whenever a different lead is opened.
  useEffect(() => {
    if (lead) {
      setStatus(normalizeStatus(lead.status));
      setNotes(lead.internal_notes || "");
    }
  }, [lead]);

  const dirty = !!lead && (status !== normalizeStatus(lead.status) || notes !== (lead.internal_notes || ""));

  const save = async () => {
    if (!lead || !dirty) return;
    setSaving(true);
    try {
      const patch = {};
      if (status !== normalizeStatus(lead.status)) patch.status = status;
      if (notes !== (lead.internal_notes || "")) patch.internal_notes = notes;
      await onSave(lead.id, patch);
      toast.success(t.admin.saved);
    } catch {
      toast.error(t.admin.saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl bg-white" data-testid="lead-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3 font-heading text-xl text-[var(--brand-navy)]">
            {t.admin.leadDetails}
            {lead && <StatusBadge status={lead.status} testId="lead-details-status-badge" />}
          </DialogTitle>
          <DialogDescription className="sr-only">{t.admin.notesHint}</DialogDescription>
        </DialogHeader>
        {lead && (
          <div className="space-y-3 text-sm">
            <Row label={t.admin.colName} value={lead.name} />
            <Row label={t.portal.phone} value={lead.phone} />
            <Row label={t.portal.email} value={lead.email} className="break-all" />
            <Row label={t.admin.colMessage} value={lead.message} className="whitespace-pre-wrap" />
            <Row label={t.admin.colDate} value={fmtDate(lead.created_at)} />
            <Row label={t.admin.colDocs} value={lead.document_count || 0} />

            {/* Admin-only controls */}
            <div className="mt-2 space-y-4 rounded-2xl border border-[color:var(--border-subtle)] bg-[var(--surface-2)] p-4">
              <div>
                <Label htmlFor="lead-status" className="text-sm font-medium text-[var(--brand-charcoal)]">{t.admin.statusLabel}</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger id="lead-status" className="mt-1.5 h-10 rounded-xl bg-white" data-testid="lead-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {LEAD_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`lead-status-option-${s}`}>{t.admin.statuses[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="lead-notes" className="flex items-center gap-1.5 text-sm font-medium text-[var(--brand-charcoal)]">
                  <StickyNote className="h-4 w-4 text-[var(--brand-burgundy)]" /> {t.admin.notesLabel}
                </Label>
                <Textarea
                  id="lead-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
                  maxLength={NOTES_MAX}
                  rows={4}
                  placeholder={t.admin.notesPlaceholder}
                  className="mt-1.5 resize-none rounded-xl bg-white"
                  data-testid="lead-notes-input"
                />
                <div className="mt-1 flex items-center justify-between text-xs text-[var(--muted-ink)]">
                  <span>{t.admin.notesHint}</span>
                  <span data-testid="lead-notes-counter">{notes.length}/{NOTES_MAX}</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={save}
                disabled={!dirty || saving}
                className="h-10 w-full rounded-xl bg-[var(--brand-burgundy)] font-semibold text-white hover:bg-[var(--brand-burgundy-dark)] disabled:opacity-60"
                data-testid="lead-save-button"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? t.admin.saving : t.admin.save}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
